import { signal } from '@preact/signals-core';

export interface UserProfile {
  id: string;
  email: string;
  role: UserRole;
  customerId?: string;
  sites: string[];
}

export type UserRole = 'superadmin' | 'admin' | 'manager' | 'technician' | 'viewer';

export interface JWTClaims {
  sub: string;
  email?: string;
  role: UserRole;
  customer_id?: string;
  sites?: string[];
  exp?: number;
}

export const accessTokenSignal = signal<string | null>(null);
export const userProfileSignal = signal<UserProfile | null>(null);
export const authLoadingSignal = signal<boolean>(true);

// Keep track of the original window fetch
const originalFetch = window.fetch;

let refreshTimer: any = null;
let refreshingPromise: Promise<string> | null = null;

// Parse the payload from JWT token
export function decodeJWT(token: string): JWTClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64Url = parts[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Failed to decode JWT:', e);
    return null;
  }
}

// Set authenticated session state
export function setSession(token: string) {
  const claims = decodeJWT(token);
  if (!claims) {
    clearSession();
    return;
  }

  accessTokenSignal.value = token;
  userProfileSignal.value = {
    id: claims.sub,
    email: claims.email || '',
    role: claims.role,
    customerId: claims.customer_id,
    sites: claims.sites || [],
  };

  scheduleSilentRefresh(token);
}

// Clear active session and any scheduled timers
export function clearSession() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  accessTokenSignal.value = null;
  userProfileSignal.value = null;
}

// Schedule token refresh 30 seconds before access token expires
export function scheduleSilentRefresh(token: string) {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  const claims = decodeJWT(token);
  if (!claims || !claims.exp) return;

  // Skip scheduling for local mock developer tokens
  if (claims.sub === 'user_dev') {
    console.log('[Auth] Bypassing silent refresh timer for local developer session.');
    return;
  }

  const expiresAtMs = claims.exp * 1000;
  const nowMs = Date.now();
  const refreshBeforeMs = 30 * 1000; // 30 seconds
  let delayMs = expiresAtMs - nowMs - refreshBeforeMs;

  if (delayMs < 0) {
    delayMs = 0; // Trigger refresh immediately if near or past expiration
  }

  console.log(`[Auth] Scheduling silent refresh in ${Math.round(delayMs / 1000)} seconds.`);
  refreshTimer = setTimeout(async () => {
    try {
      console.log('[Auth] Triggering scheduled silent token refresh...');
      await refreshAccessToken();
    } catch (e) {
      console.error('[Auth] Scheduled token refresh failed:', e);
    }
  }, delayMs);
}

// Invoke the refresh token endpoint and resolve with the new token
export async function refreshAccessToken(): Promise<string> {
  if (refreshingPromise) {
    return refreshingPromise;
  }

  refreshingPromise = (async () => {
    try {
      const res = await originalFetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (res.ok) {
        const data = await res.json();
        const token = data.accessToken;
        setSession(token);
        console.log('[Auth] Token refresh successful.');
        return token;
      } else {
        throw new Error(`HTTP ${res.status} on refresh`);
      }
    } catch (e) {
      console.error('[Auth] Silent refresh failed:', e);
      clearSession();
      if (window.location.hash !== '#login') {
        window.location.hash = '#login';
      }
      throw e;
    } finally {
      refreshingPromise = null;
    }
  })();

  return refreshingPromise;
}

// Initialize global window fetch interceptor to append authorization headers and capture 401s
export function setupFetchInterceptor() {
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const urlString = typeof input === 'string'
      ? input
      : (input instanceof URL ? input.href : input.url);
    const isApiRequest = urlString.startsWith('/api/') || urlString.includes('/api/v1/');

    let headers = new Headers(init?.headers);

    if (isApiRequest && accessTokenSignal.value) {
      if (!headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${accessTokenSignal.value}`);
      }
    }

    const config: RequestInit = {
      ...init,
      headers,
    };

    try {
      const response = await originalFetch(input, config);

      if (response.status === 401 && isApiRequest) {
        const isAuthRequest = urlString.includes('/api/v1/auth/login') || urlString.includes('/api/v1/auth/refresh');
        if (!isAuthRequest) {
          console.warn('[Auth] Access token expired or invalid (401). Retrying with fresh session...');
          try {
            const newToken = await refreshAccessToken();
            const retryHeaders = new Headers(config.headers);
            retryHeaders.set('Authorization', `Bearer ${newToken}`);
            return await originalFetch(input, { ...config, headers: retryHeaders });
          } catch (refreshErr) {
            console.error('[Auth] Failed to retrieve fresh session during interceptor retry:', refreshErr);
            return response;
          }
        }
      }

      return response;
    } catch (err) {
      throw err;
    }
  };
}
